import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  FormContainer,
  NextButton,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  RadioInput,
  LaurenQuoteMobile,
  StringInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { requiredString } from "~/utils/validations";

export type HasMedicalDiagnosis = "" | "Yes" | "No";

type Values = {
  hasMedicalConditions: HasMedicalDiagnosis;
  relevantMedicalConditions: string;
};

const values: HasMedicalDiagnosis[] = ["Yes", "No"];

const validationSchema = Yup.object({
  hasMedicalConditions: requiredString.oneOf(values),
  relevantMedicalConditions: Yup.string(),
});

export const MedicalDiagnosisPage: FC = () => {
  const { t } = useTranslation();
  const { hasMedicalConditions, relevantMedicalConditions } = useOnboarding();
  const { onSubmit } = useSubmitOnboardingQuestion();

  const initialValues: Values = {
    hasMedicalConditions: hasMedicalConditions || "",
    relevantMedicalConditions: relevantMedicalConditions || "",
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnMount: true,
  });

  const showAdditionalQuestion = formik.values.hasMedicalConditions === "Yes";
  const isAutoAdvance = (option: string) => option == "No";
  const showButton = showAdditionalQuestion || !!hasMedicalConditions;

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt
            comment={t("onboarding.medicalDiagnosisPage.comment")}
            description={t("onboarding.medicalDiagnosisPage.description")}
            question={t("onboarding.medicalDiagnosisPage.question")}
          />
          <LaurenQuoteMobile>
            {t("onboarding.medicalDiagnosisPage.comment")}
          </LaurenQuoteMobile>

          <RadioInput
            autoAdvance={isAutoAdvance}
            defaultValue={hasMedicalConditions}
            id="hasMedicalConditions"
            values={values}
          />
        </QuestionContainer>

        {showAdditionalQuestion && (
          <QuestionContainer my={5}>
            <Prompt
              question={t(
                "onboarding.medicalDiagnosisPage.additionalQuestion.question",
              )}
            />

            <StringInput
              id="relevantMedicalConditions"
              placeholder={t(
                "onboarding.medicalDiagnosisPage.additionalQuestion.placeholder",
              )}
            />
          </QuestionContainer>
        )}

        <NextButton showButton={showButton} />
      </FormContainer>
    </QuestionPageContainer>
  );
};
